import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import authService from '../../services/authService';
import { Button, Input, Alert } from '../ui';
import { useCountry } from '../../hooks/useCountry';

// Step Indicator Component
const StepIndicator = ({ currentStep, totalSteps, colors }) => (
  <div className="flex items-center justify-center mb-6 sm:mb-8">
    {Array.from({ length: totalSteps }, (_, i) => (
      <div key={i} className="flex items-center">
        <div
          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm transition-all duration-300 ${
            i < currentStep ? 'bg-success-500 text-white' : i > currentStep ? 'bg-gray-200 text-gray-500' : 'text-white shadow-lg'
          }`}
          style={i === currentStep ? { background: colors.buttonBg || 'linear-gradient(135deg, var(--primary-600), var(--primary-700))', boxShadow: `0 10px 25px -5px ${colors.primary || 'var(--primary-500)'}40` } : undefined}
        >
          {i < currentStep ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (i + 1)}
        </div>
        {i < totalSteps - 1 && (
          <div className={`w-8 sm:w-16 h-1 mx-1 sm:mx-2 rounded-full transition-colors duration-300 ${i < currentStep ? 'bg-success-500' : 'bg-gray-200'}`} />
        )}
      </div>
    ))}
  </div>
);

// Password Strength Meter
const PasswordStrength = ({ password }) => {
  const { t } = useTranslation();
  const getStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const strength = getStrength();
  const labels = [t('auth.register.strengthVeryWeak'), t('auth.register.strengthWeak'), t('auth.register.strengthFair'), t('auth.register.strengthStrong'), t('auth.register.strengthVeryStrong')];
  const barColors = ['bg-error-500', 'bg-warning-500', 'bg-amber-400', 'bg-success-400', 'bg-success-500'];

  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < strength ? barColors[strength - 1] : 'bg-gray-200'}`} />
        ))}
      </div>
      <p className={`text-xs ${strength <= 2 ? 'text-warning-600' : 'text-success-600'}`}>
        {labels[strength - 1] || 'Very Weak'}
      </p>
    </div>
  );
};

export default function Register() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '', username: '', first_name: '', last_name: '', password: '', password2: ''
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { theme, country: countryConfig, isSubdomain } = useCountry();
  const { t, i18n } = useTranslation();
  const colors = theme?.colors || {};

  // Set language from country config on subdomain
  useEffect(() => {
    if (isSubdomain && countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [isSubdomain, countryConfig, i18n]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};
    if (stepIndex === 0) {
      if (!formData.email) newErrors.email = t('auth.register.emailRequired');
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('auth.register.emailInvalid');
      if (!formData.username) newErrors.username = t('auth.register.usernameRequired');
      else if (formData.username.length < 3) newErrors.username = t('auth.register.usernameMinLength');
    }
    if (stepIndex === 1) {
      if (!formData.first_name) newErrors.first_name = t('auth.register.firstNameRequired');
      if (!formData.last_name) newErrors.last_name = t('auth.register.lastNameRequired');
    }
    if (stepIndex === 2) {
      if (!formData.password) newErrors.password = t('auth.register.passwordRequired');
      else if (formData.password.length < 8) newErrors.password = t('auth.register.passwordMinLength');
      if (!formData.password2) newErrors.password2 = t('auth.register.passwordConfirmRequired');
      else if (formData.password !== formData.password2) newErrors.password2 = t('auth.register.passwordMismatch');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => { if (validateStep(step)) setStep(prev => prev + 1); };
  const handleBack = () => { setStep(prev => prev - 1); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(2)) return;
    setApiError('');
    setLoading(true);
    try {
      await authService.register(formData);
      await authService.login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData) {
        const errorMessages = Object.entries(errorData)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join('\n');
        setApiError(errorMessages);
      } else {
        setApiError(t('auth.register.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = [t('auth.register.stepAccountTitle'), t('auth.register.stepProfileTitle'), t('auth.register.stepSecurityTitle')];
  const stepDescriptions = [t('auth.register.stepAccountDesc'), t('auth.register.stepProfileDesc'), t('auth.register.stepSecurityDesc')];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 mb-6 sm:mb-8 group">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))', boxShadow: theme ? `0 10px 25px -5px ${colors.primary}40` : undefined }}>
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className={`text-xl sm:text-2xl font-bold ${theme ? '' : 'gradient-text'}`} style={theme ? { color: colors.primary } : undefined}>RentyDoc</span>
          </Link>

          {/* Step Indicator */}
          <StepIndicator currentStep={step} totalSteps={3} colors={colors} />

          {/* Header */}
          <div className="mb-6 sm:mb-8 text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{stepTitles[step]}</h1>
            <p className="text-gray-600 text-sm sm:text-base">{stepDescriptions[step]}</p>
          </div>

          {/* Error Alert */}
          {apiError && (
            <Alert type="error" className="mb-4 sm:mb-6" dismissible onDismiss={() => setApiError('')}>
              <pre className="text-sm whitespace-pre-wrap">{apiError}</pre>
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className={`space-y-4 sm:space-y-6 ${step === 0 ? 'block animate-slide-up' : 'hidden'}`}>
              <Input label={t('auth.register.emailLabel')} type="email" name="email" value={formData.email} onChange={handleChange} error={errors.email} required placeholder={t('auth.login.emailPlaceholder')} />
              <Input label={t('auth.register.usernameLabel')} type="text" name="username" value={formData.username} onChange={handleChange} error={errors.username} required placeholder={t('auth.register.usernamePlaceholder')} helperText={t('auth.register.usernameHelper')} />
            </div>
            <div className={`space-y-4 sm:space-y-6 ${step === 1 ? 'block animate-slide-up' : 'hidden'}`}>
              <Input label={t('auth.register.firstNameLabel')} type="text" name="first_name" value={formData.first_name} onChange={handleChange} error={errors.first_name} required placeholder={t('auth.register.firstNamePlaceholder')} />
              <Input label={t('auth.register.lastNameLabel')} type="text" name="last_name" value={formData.last_name} onChange={handleChange} error={errors.last_name} required placeholder={t('auth.register.lastNamePlaceholder')} />
            </div>
            <div className={`space-y-4 sm:space-y-6 ${step === 2 ? 'block animate-slide-up' : 'hidden'}`}>
              <div>
                <Input label={t('auth.register.passwordLabel')} type="password" name="password" value={formData.password} onChange={handleChange} error={errors.password} required placeholder={t('auth.register.passwordPlaceholder')} />
                <PasswordStrength password={formData.password} />
              </div>
              <Input label={t('auth.register.confirmPasswordLabel')} type="password" name="password2" value={formData.password2} onChange={handleChange} error={errors.password2} required placeholder={t('auth.register.confirmPasswordPlaceholder')} />
            </div>

            <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
              {step > 0 && <Button type="button" variant="outline" onClick={handleBack} className="flex-1 !py-3">{t('common.back')}</Button>}
              {step < 2 ? (
                <Button type="button" variant="primary" onClick={handleNext} className="flex-1 !py-3">{t('auth.register.continue')}</Button>
              ) : (
                <Button type="submit" variant="primary" loading={loading} className="flex-1 !py-3">{t('auth.register.createAccount')}</Button>
              )}
            </div>
          </form>

          <p className="text-center mt-6 sm:mt-8 text-gray-600 text-sm sm:text-base">
            {t('auth.register.alreadyHaveAccount')}{' '}
            <Link to="/login" className="font-semibold" style={{ color: colors.primary || 'var(--primary-600)' }}>{t('auth.login.signIn')}</Link>
          </p>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden" style={{ background: theme ? colors.heroBg : undefined }}>
        {!theme && <div className="absolute inset-0 hero-gradient-bg" />}
        <div className="absolute w-96 h-96 rounded-full -top-48 -right-48 blur-3xl float-animation" style={{ backgroundColor: theme ? `${colors.primaryLight}40` : 'rgba(var(--primary-400-rgb), 0.3)' }} />
        <div className="absolute w-80 h-80 rounded-full bottom-1/4 -left-40 blur-3xl float-animation-delayed" style={{ backgroundColor: theme ? `${colors.accent || colors.secondary}30` : 'rgba(var(--accent-400-rgb), 0.3)' }} />

        <div className="relative z-10 flex flex-col items-center justify-center w-full p-16">
          <div className="glass-card p-8 mb-8 max-w-md text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))' }}>
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Join RentyDoc Today</h2>
            <p className="text-gray-600">Create your free account and start generating professional rental contracts in minutes.</p>
          </div>

          <div className="space-y-4">
            {['Legally compliant contracts', '8 countries supported', 'Instant PDF download', 'Secure & encrypted'].map((benefit, i) => (
              <div key={i} className="flex items-center glass-card !p-3">
                <svg className="w-5 h-5 text-success-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
